import { store } from "../../utils/store";
import urls from "../../global/constants/UrlConstants";

/** True when frontend is pointed at QA API. */
export const isQaApi = (): boolean =>
  /qa-api/i.test(String(urls?.url_prod || ""));

/** True when frontend is pointed at production API. */
export const isProdApi = (): boolean => {
  const host = String(urls?.url_prod || "");
  return /api\.yhataw\.com/i.test(host) && !/qa-api/i.test(host);
};

/**
 * Leave / Attendance under Team are available on QA and local/dev,
 * but intentionally hidden when pointed at production API.
 */
export const canShowHrTeamMenus = (): boolean => !isProdApi();

const HR_LEAF_KEYS = new Set([
  "All Leaves",
  "Leave",
  "Salary",
  "All Attendance",
  "Attendance",
]);

const HR_GROUP_NAMES = new Set(["Leaves", "Attendance"]);

export const isHrTeamGroup = (group: any): boolean =>
  HR_GROUP_NAMES.has(String(group?.subMenuPageName || ""));

export const isHrLeafKey = (key?: string): boolean =>
  !!key && HR_LEAF_KEYS.has(key);

/** Actions that count as access for sidebar visibility (matches screen checks). */
const ACCESS_ACTIONS = new Set([
  "view",
  "create",
  "edit",
  "delete",
  "download",
  "export",
  "import",
  "see all",
  "full control",
]);

const isAccessAction = (value: unknown): boolean => {
  if (typeof value !== "string") return false;
  return ACCESS_ACTIONS.has(value.trim());
};

/**
 * Settings sidebar leaves — must match Role privilege matrix keys
 * (Setting → children in AddRole MODULE_TREE).
 */
export const SETTING_LEAF_KEYS = new Set([
  "Account",
  "Meta Data",
  "Organizations",
  "organizations",
  "Integrations",
  "integrations",
  "AuditLogs",
  "Audit Logs",
  "audit-logs",
  "Automation",
  "Automation Lead Flow",
  "automation",
  "Roles & Privileges",
  "System Settings",
  "Profile",
  "Security",
  "CRM Preferences",
]);

/** Tenant-org admin leaves — only unlocked for true super admins without role keys. */
const ORG_ADMIN_LEAF_KEYS = new Set([
  "Organizations",
  "organizations",
  "Integrations",
  "integrations",
  "AuditLogs",
  "audit-logs",
  "Audit Logs",
  "Automation",
  "Automation Lead Flow",
  "automation",
]);

export const isOrgAdminLeafKey = (key?: string): boolean =>
  !!key && ORG_ADMIN_LEAF_KEYS.has(key);

export const isSettingLeafKey = (key?: string): boolean =>
  !!key && SETTING_LEAF_KEYS.has(key);

/** Platform super admin only (not every team admin / hierarchy-1). */
export const isTrueSuperAdminUser = (): boolean => {
  const auth = store.getState().auth || {};
  if (auth.isSuperAdmin) return true;
  const memberships = auth.memberships || [];
  return memberships.some((m: any) => m?.roleSlug === "super_admin");
};

/** @deprecated Prefer isTrueSuperAdminUser — kept for rare org-admin tooling. */
export const isSuperAdminUser = (): boolean => {
  if (isTrueSuperAdminUser()) return true;
  const auth = store.getState().auth || {};
  if (!!auth.isAdmin || String(auth.hierarchyLevel) === "1") return true;
  const memberships = auth.memberships || [];
  return memberships.some((m: any) =>
    ["org_admin", "sub_admin", "super_admin"].includes(m?.roleSlug)
  );
};

/** True when privilege map grants real access for this module/leaf key. */
export const hasPrivilegeKey = (
  privileges: Record<string, any> | undefined | null,
  key?: string
): boolean => {
  if (!privileges || !key) return false;
  const access = privileges[key];
  if (access == null) return false;

  if (Array.isArray(access)) {
    return access.some(isAccessAction);
  }

  if (typeof access === "string") {
    return isAccessAction(access);
  }

  return false;
};

/** Alias keys used across menu / role matrix / account cards. */
const PRIVILEGE_ALIASES: Record<string, string[]> = {
  AuditLogs: ["Audit Logs", "audit-logs"],
  "Audit Logs": ["AuditLogs", "audit-logs"],
  Automation: ["Automation Lead Flow", "automation"],
  "Automation Lead Flow": ["Automation", "automation"],
  Organizations: ["organizations"],
  organizations: ["Organizations"],
  Integrations: ["integrations", "Org Integrations"],
  "System Settings": ["System Setting"],
  "System Setting": ["System Settings"],
  "Roles & Privileges": ["Role", "Roles"],
  Role: ["Roles & Privileges", "Roles"],
  "CRM Preferences": ["Account Status"],
  "Account Status": ["CRM Preferences"],
  Profile: ["User Profile"],
  "User Profile": ["Profile"],
  Marketing: ["WhatsApp Marketing", "WhatsApp Instant Trigger"],
  "WhatsApp Marketing": ["Marketing", "WhatsApp Instant Trigger"],
  "WhatsApp Instant Trigger": ["Marketing", "WhatsApp Marketing"],
  "Leads Bulk Upload": [
    "Bulk Upload Project",
    "Bulk Upload Master",
    "Bulk Upload",
    "Leads Bulk Upload Sets",
  ],
  "Leads Bulk Upload Sets": [
    "Leads Bulk Upload",
    "Bulk Upload Project",
    "Bulk Upload Master",
    "Bulk Upload",
  ],
  "Calling Report": ["Call Logs", "Daily Calling Report", "Call-logs", "Calling-report", "Report"],
  "Daily Calling Report": ["Calling Report", "Call Logs", "Call-logs", "Calling-report", "Report"],
  "Call Logs": ["Calling Report", "Daily Calling Report", "Call-logs", "Calling-report"],
  "Call-logs": ["Call Logs", "Calling Report", "Daily Calling Report"],
  "Calling-report": ["Calling Report", "Daily Calling Report", "Call Logs"],
  "Call Datasets": [
    "Upload Call Data",
    "Leads",
    "All Lead",
    "My Leads",
    "My Lead",
    "Team Leads",
    "Leads Bulk Upload",
  ],
  "Upload Call Data": [
    "Call Datasets",
    "Leads",
    "All Lead",
    "My Leads",
    "My Lead",
    "Team Leads",
    "Leads Bulk Upload",
  ],
  "Bulk Upload Project": ["Leads Bulk Upload", "Leads Bulk Upload Sets"],
  "Bulk Upload Master": ["Leads Bulk Upload", "Leads Bulk Upload Sets"],
  "Channel Partner": [
    "Channel Partners",
    "CP Bulk Upload",
    "Add CP Lead",
  ],
  "Channel Partners": ["Channel Partner", "CP Bulk Upload", "Add CP Lead"],
  "CP Bulk Upload": ["Channel Partner", "Channel Partners"],
  "Add CP Lead": ["Channel Partner", "Channel Partners"],
  "My Leads": ["My Lead", "Leads", "All Lead"],
  "My Lead": ["My Leads", "Leads", "All Lead"],
};

export const hasPrivilegeKeyOrAlias = (
  privileges: Record<string, any> | undefined | null,
  key?: string
): boolean => {
  if (!key) return false;
  if (hasPrivilegeKey(privileges, key)) return true;
  return (PRIVILEGE_ALIASES[key] || []).some((alias) =>
    hasPrivilegeKey(privileges, alias)
  );
};

/** Collect leaf keys under a menu (subMenu → subSubMenu). */
export const getMenuLeafKeys = (menu: any): string[] => {
  const keys: string[] = [];
  (menu?.subMenu || []).forEach((group: any) => {
    if (group?.key) keys.push(group.key);
    (group?.subSubMenu || []).forEach((leaf: any) => {
      if (leaf?.key) keys.push(leaf.key);
    });
  });
  return keys;
};

/** True when the map grants a specific action (not just "any access") on a module. */
export const hasPrivilegeAction = (
  privileges: Record<string, any> | undefined | null,
  moduleKey: string,
  actions: string[]
): boolean => {
  const granted = privileges?.[moduleKey];
  const list = Array.isArray(granted)
    ? granted
    : typeof granted === "string"
      ? [granted]
      : [];
  const normalized = list.map((a) => String(a || "").trim().toLowerCase());
  if (normalized.includes("full control")) return true;
  return actions.some((a) => normalized.includes(a.toLowerCase()));
};

/**
 * Payments sidebar, Payments page, and lead list payment column.
 * True platform super_admin only, unless Payment view/send is granted on Team.
 */
export const canViewPayments = (
  privilegesOverride?: Record<string, any> | undefined | null
): boolean => {
  if (isTrueSuperAdminUser()) return true;
  const privileges =
    privilegesOverride || store.getState().auth?.privileges || {};
  return (
    hasPrivilegeAction(privileges, "Payment", [
      "view",
      "edit",
      "create",
      "full control",
    ]) ||
    hasPrivilegeAction(privileges, "Payments", [
      "view",
      "edit",
      "create",
      "full control",
    ])
  );
};

export const canSendPaymentLinks = (
  privilegesOverride?: Record<string, any> | undefined | null
): boolean => {
  if (isTrueSuperAdminUser()) return true;
  const privileges =
    privilegesOverride || store.getState().auth?.privileges || {};
  return (
    hasPrivilegeAction(privileges, "Payment", [
      "edit",
      "create",
      "full control",
    ]) ||
    hasPrivilegeAction(privileges, "Payments", [
      "edit",
      "create",
      "full control",
    ])
  );
};

/** Admins always allowed to open Marketing even without role matrix ticks. */
const MARKETING_ADMIN_EMAILS = new Set([
  "arjunnanda@yhataw.com",
  "crm@yhataw.com", // Nazim Khan
]);
const MARKETING_ADMIN_USER_IDS = new Set([
  "679378e50fb7ee4b2c477927", // Arjun Nanda
  "67a87b33a5b6a25d16c0c994", // Nazim Khan
]);

/**
 * Marketing / WhatsApp Marketing sidebar + pages.
 * Visible only to Arjun / Nazim (allowlisted admins), true super admin,
 * or a role/user with explicit Marketing privilege — not every logged-in user.
 */
export const canViewMarketing = (
  privilegesOverride?: Record<string, any> | undefined | null
): boolean => {
  if (isTrueSuperAdminUser()) return true;

  const auth = store.getState().auth || {};
  const email = String(auth.email || "")
    .trim()
    .toLowerCase();
  const userId = String(auth.userId || "").trim();
  if (
    (email && MARKETING_ADMIN_EMAILS.has(email)) ||
    (userId && MARKETING_ADMIN_USER_IDS.has(userId))
  ) {
    return true;
  }

  const privileges =
    privilegesOverride || auth.privileges || {};
  return (
    hasPrivilegeKeyOrAlias(privileges, "Marketing") ||
    hasPrivilegeKeyOrAlias(privileges, "WhatsApp Marketing") ||
    hasPrivilegeKeyOrAlias(privileges, "WhatsApp Instant Trigger")
  );
};

/**
 * Lead export gate. Admins always have it; anyone else needs "export" (or
 * "download") ticked on the lead module in Roles & Privileges, or granted
 * directly on their team profile.
 *
 * Mirrors requirePrivilege("All Lead", ["export","download"]) on the server —
 * this only hides the button, the backend does the real enforcement.
 */
export const canExportLeadsFor = (
  privileges: Record<string, any> | undefined | null,
  moduleKey: string = "All Lead"
): boolean => {
  if (isSuperAdminUser()) return true;
  // "All Lead" is the key the admin grant toggle writes to, so treat it as the
  // umbrella permission for every lead list (each of which shows a subset).
  const keys = Array.from(
    new Set([moduleKey, ...(PRIVILEGE_ALIASES[moduleKey] || []), "All Lead"])
  );
  return keys.some((key) =>
    hasPrivilegeAction(privileges, key, ["export", "download"])
  );
};

/**
 * Explicit Setting submodule access — parent "Setting" alone is NOT enough.
 * True platform super_admin bypasses the matrix.
 */
export const hasSettingModuleAccess = (
  moduleKey: string,
  privilegesOverride?: Record<string, any>
): boolean => {
  if (isTrueSuperAdminUser()) return true;
  const privileges =
    privilegesOverride || store.getState().auth?.privileges || {};
  return hasPrivilegeKeyOrAlias(privileges, moduleKey);
};

/** Organizations page / menu — requires Organizations privilege only. */
export const canAccessOrganizations = (
  privilegesOverride?: Record<string, any>
): boolean => hasSettingModuleAccess("Organizations", privilegesOverride);

/**
 * Show a top-level sidebar item when:
 * - its own key is privileged, OR
 * - any submodule / leaf under it is privileged
 *
 * Setting: shown only when Setting parent OR a specific Setting submodule
 * is privileged (true super_admin may see all).
 */
export const canShowMenuItem = (menu: any): boolean => {
  // Admins and super admins can see every sidebar item
  if (isSuperAdminUser()) return true;

  const privileges = store.getState().auth?.privileges || {};
  const menuKey = String(menu?.key || "");

  if (menuKey === "Setting") {
    if (hasPrivilegeKeyOrAlias(privileges, "Setting")) return true;
    return getMenuLeafKeys(menu).some((key) =>
      hasPrivilegeKeyOrAlias(privileges, key)
    );
  }

  if (hasPrivilegeKeyOrAlias(privileges, menu?.key)) return true;
  // Meeting — unlock for platform super admin / Meeting List / Booking keys
  if (
    menu?.key === "Meeting" &&
    (isTrueSuperAdminUser() ||
      hasPrivilegeKeyOrAlias(privileges, "Meeting") ||
      hasPrivilegeKeyOrAlias(privileges, "Meeting List") ||
      hasPrivilegeKeyOrAlias(privileges, "Booking"))
  ) {
    return true;
  }
  // Feedback / Registrations share access with Lead Form until roles are updated
  if (
    menu?.key === "Feedback" &&
    (hasPrivilegeKeyOrAlias(privileges, "Lead Form") ||
      hasPrivilegeKeyOrAlias(privileges, "Feedback") ||
      isTrueSuperAdminUser())
  ) {
    return true;
  }
  if (
    menu?.key === "Registrations" &&
    (hasPrivilegeKeyOrAlias(privileges, "Lead Form") ||
      hasPrivilegeKeyOrAlias(privileges, "Registrations") ||
      hasPrivilegeKeyOrAlias(privileges, "Registration") ||
      isTrueSuperAdminUser())
  ) {
    return true;
  }
  // User Manual — visible to all authenticated users (documentation resource)
  if (menu?.key === "User Manual") return true;

  if (menu?.key === "Marketing" || menu?.key === "WhatsApp Marketing") {
    return canViewMarketing(privileges);
  }

  if (menu?.key === "Payment") {
    return canViewPayments(privileges);
  }
  // Channel Partner — platform super admin always sees it; role keys unlock for others
  if (
    (menu?.key === "Channel Partner" || menu?.key === "Channel Partners") &&
    (isTrueSuperAdminUser() ||
      hasPrivilegeKeyOrAlias(privileges, "Channel Partner") ||
      hasPrivilegeKeyOrAlias(privileges, "Channel Partners") ||
      hasPrivilegeKeyOrAlias(privileges, "CP Bulk Upload") ||
      hasPrivilegeKeyOrAlias(privileges, "Add CP Lead"))
  ) {
    return true;
  }
  // Leads parent — super admin / All Lead / Lead Form unlocks the group
  if (
    menu?.key === "Leads" &&
    (isTrueSuperAdminUser() ||
      hasPrivilegeKeyOrAlias(privileges, "Leads") ||
      hasPrivilegeKeyOrAlias(privileges, "All Lead") ||
      hasPrivilegeKeyOrAlias(privileges, "Lead Form") ||
      hasPrivilegeKeyOrAlias(privileges, "Leads Bulk Upload"))
  ) {
    return true;
  }
  return getMenuLeafKeys(menu).some((key) =>
    hasPrivilegeKeyOrAlias(privileges, key)
  );
};

/**
 * Leaf visible in secondary panel.
 *
 * Setting leaves require that exact module's privilege.
 * Parent "Setting" does NOT unlock Organizations / Meta Data / etc.
 */
export const canShowLeafItem = (
  leaf: any,
  parentMenu?: any,
  privilegesOverride?: Record<string, any>
): boolean => {
  const privileges =
    privilegesOverride || store.getState().auth?.privileges || {};
  const parentKey = parentMenu?.key;
  const leafKey = leaf?.key;

  // Prod: never surface Leave / Attendance under Team
  if (parentKey === "Team" && isHrLeafKey(leafKey) && !canShowHrTeamMenus()) {
    return false;
  }

  // Settings modules — each submodule needs its own privilege
  if (parentKey === "Setting" || isSettingLeafKey(leafKey)) {
    if (!leafKey) return false;
    return hasSettingModuleAccess(String(leafKey), privileges);
  }

  if (leafKey === "Payment") {
    return canViewPayments(privileges);
  }

  if (
    parentKey === "Marketing" ||
    leafKey === "WhatsApp Marketing" ||
    leafKey === "WhatsApp Instant Trigger" ||
    leafKey === "Marketing"
  ) {
    return canViewMarketing(privileges);
  }

  // Admins and super admins can see every leaf item
  if (isSuperAdminUser()) return true;

  if (hasPrivilegeKeyOrAlias(privileges, leafKey)) return true;

  // Daily calling report sits next to Call Logs; existing roles often only tick Call Logs / Report
  if (
    (leafKey === "Daily Calling Report" ||
      leafKey === "Calling Report" ||
      leafKey === "Call Logs") &&
    (hasPrivilegeKeyOrAlias(privileges, "Call Logs") ||
      hasPrivilegeKeyOrAlias(privileges, "Calling Report") ||
      hasPrivilegeKeyOrAlias(privileges, "Daily Calling Report") ||
      hasPrivilegeKeyOrAlias(privileges, "Report"))
  ) {
    return true;
  }

  // Super admin unlocks Meeting / Booking leaves
  if (
    (parentKey === "Meeting" ||
      leafKey === "Meeting List" ||
      leafKey === "Booking" ||
      leafKey === "Meeting") &&
    isTrueSuperAdminUser()
  ) {
    return true;
  }

  if (
    (leafKey === "Call Datasets" || leafKey === "Upload Call Data") &&
    (isTrueSuperAdminUser() ||
      hasPrivilegeKeyOrAlias(privileges, "Call Datasets") ||
      hasPrivilegeKeyOrAlias(privileges, "All Lead") ||
      hasPrivilegeKeyOrAlias(privileges, "Leads") ||
      hasPrivilegeKeyOrAlias(privileges, "My Leads") ||
      hasPrivilegeKeyOrAlias(privileges, "Team Leads") ||
      hasPrivilegeKeyOrAlias(privileges, "Leads Bulk Upload"))
  ) {
    return true;
  }

  // Super admin / All Lead unlocks Leads Bulk Upload leaves (create + sets)
  if (
    (leafKey === "Leads Bulk Upload" ||
      leafKey === "Leads Bulk Upload Sets") &&
    (isTrueSuperAdminUser() ||
      hasPrivilegeKeyOrAlias(privileges, "Leads Bulk Upload") ||
      hasPrivilegeKeyOrAlias(privileges, "Leads Bulk Upload Sets") ||
      hasPrivilegeKeyOrAlias(privileges, "Bulk Upload Project") ||
      hasPrivilegeKeyOrAlias(privileges, "Bulk Upload Master") ||
      hasPrivilegeKeyOrAlias(privileges, "All Lead") ||
      hasPrivilegeKeyOrAlias(privileges, "Leads"))
  ) {
    return true;
  }

  // Channel Partner list access also unlocks Add CP Lead / CP Bulk Upload leaves
  if (
    (parentKey === "Channel Partner" || parentKey === "Channel Partners") &&
    ["Add CP Lead", "CP Bulk Upload", "Channel Partners"].includes(
      String(leafKey || "")
    ) &&
    (isTrueSuperAdminUser() ||
      hasPrivilegeKeyOrAlias(privileges, "Channel Partners") ||
      hasPrivilegeKeyOrAlias(privileges, "Channel Partner") ||
      hasPrivilegeKeyOrAlias(privileges, "CP Bulk Upload") ||
      hasPrivilegeKeyOrAlias(privileges, "Add CP Lead"))
  ) {
    return true;
  }

  // WhatsApp Marketing leaf handled above via canViewMarketing

  // QA / local: Team (or Team List) access unlocks Leave / Attendance leaves
  if (
    parentKey === "Team" &&
    canShowHrTeamMenus() &&
    isHrLeafKey(leafKey) &&
    (hasPrivilegeKeyOrAlias(privileges, "Team") ||
      hasPrivilegeKeyOrAlias(privileges, "Team List"))
  ) {
    return true;
  }

  const parentCoversChildren = ["Meeting", "Project", "project"].includes(
    String(parentKey || "")
  );
  if (
    parentCoversChildren &&
    parentKey &&
    hasPrivilegeKeyOrAlias(privileges, parentKey)
  ) {
    return true;
  }
  return false;
};

/** Whether a Team secondary group should be considered on this API. */
export const canShowTeamGroup = (group: any): boolean => {
  if (!isHrTeamGroup(group)) return true;
  // Leaves / Attendance groups: QA + local/dev (hidden on prod API)
  return canShowHrTeamMenus();
};

/**
 * Account Settings hub cards — map cardbuttonValue → privilege key(s).
 * Do NOT fall back to parent "Setting" for admin modules — each card needs
 * its own module privilege. Profile/Security stay self-service.
 */
export const ACCOUNT_CARD_PRIVILEGE_KEYS: Record<string, string[]> = {
  Profile: ["Profile"],
  Security: ["Security"],
  "Organization Setup": ["Organizations"],
  "Tenant Organizations": ["Organizations"],
  "Org Integrations": ["Integrations"],
  "Audit Logs": ["AuditLogs", "Audit Logs"],
  Role: ["Roles & Privileges"],
  Roles: ["Roles & Privileges"],
  "System Setting": ["System Settings", "System Setting"],
  "Automation Lead Flow": ["Automation", "Automation Lead Flow"],
  Automation: ["Automation", "Automation Lead Flow"],
  "Account Status": ["CRM Preferences", "Account Status"],
};

export const canShowAccountSettingCard = (
  cardbuttonValue: string,
  privilegesOverride?: Record<string, any>
): boolean => {
  if (isTrueSuperAdminUser()) return true;
  const privileges =
    privilegesOverride || store.getState().auth?.privileges || {};
  // Self-service profile/security always available when logged in
  if (cardbuttonValue === "Profile" || cardbuttonValue === "Security") {
    return true;
  }
  const keys = ACCOUNT_CARD_PRIVILEGE_KEYS[cardbuttonValue] || [
    cardbuttonValue,
  ];
  return keys.some((k) => hasSettingModuleAccess(k, privileges));
};
